import { useCallback, useMemo, useRef } from "react";
import {
  LockedAutocomplete,
  type AutocompleteOption,
  type LockedAutocompleteHandle,
} from "@/components/LockedAutocomplete";
import { normalizeEmployeeLogin } from "@/lib/auth";
import { searchEmployees } from "@/lib/projectOverview";

export interface MentionedEmployee {
  login: string;
  display: string;
}

export function EmployeeMentionField({
  value,
  onChange,
  excludeLogins = [],
}: {
  value: MentionedEmployee[];
  onChange: (next: MentionedEmployee[]) => void;
  excludeLogins?: string[];
}) {
  const searchRef = useRef<LockedAutocompleteHandle | null>(null);
  const excludedKey = useMemo(
    () =>
      [...excludeLogins, ...value.map((item) => item.login)]
        .map((login) => normalizeEmployeeLogin(login))
        .filter(Boolean)
        .sort()
        .join(","),
    [excludeLogins, value],
  );

  const loadOptions = useCallback(
    async (query: string) => {
      const excluded = new Set(excludedKey.split(",").filter(Boolean));
      const q = query.replace(/^@+/, "").trim();
      const rows = await searchEmployees(q, 20);
      return rows
        .map((row): AutocompleteOption<MentionedEmployee> | null => {
          const login = normalizeEmployeeLogin(String(row.id || row.email || ""));
          if (!login || excluded.has(login)) return null;
          const display = String(row.label || row.name || login).trim() || login;
          return {
            key: login,
            label: display,
            description: row.subtitle || row.email || undefined,
            value: { login, display },
          };
        })
        .filter((opt): opt is AutocompleteOption<MentionedEmployee> => opt != null);
    },
    [excludedKey],
  );

  return (
    <div>
      <div
        className="mb-2 flex flex-wrap gap-1"
        aria-label="Mitarbeiter per @ einladen"
      >
        <button
          type="button"
          className="eh-po-chat-mention-chip"
          onClick={() => searchRef.current?.focus()}
        >
          <strong>@</strong> Mitarbeiter
        </button>
      </div>
      {value.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {value.map((person) => (
            <span
              key={person.login}
              className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2.5 py-1 text-xs text-accent"
            >
              @{person.display}
              <button
                type="button"
                onClick={() => onChange(value.filter((item) => item.login !== person.login))}
                className="rounded-full p-0.5 hover:bg-accent/15"
                aria-label={`@${person.display} entfernen`}
              >
                <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <LockedAutocomplete<MentionedEmployee>
        ref={searchRef}
        placeholder="@ Name suchen und einladen …"
        value={null}
        loadOptions={loadOptions}
        keepOpenOnSelect
        minChars={0}
        reloadKey={excludedKey}
        onChange={(opt) => {
          if (!opt) return;
          if (value.some((item) => item.login === opt.value.login)) return;
          onChange([...value, opt.value]);
        }}
      />
    </div>
  );
}
